import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getUserById, signIn as userSignIn, getUserByEmail, verifyUserPasscode, verifyPasscodeByEmail, verifyPasscodeDirectly } from '../firebase/services/userService';
import { User } from '../models/types';
import { isAuthSessionValid, updateAuthSession, notifyAuthStateChange, setCurrentUserId, getCurrentUserId, clearAuthSession, setUserRoleId, setLoggedOutState, getAuthSession } from '../utils/auth';
import { getRoleById } from '../firebase/services/roleService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Role } from '../types/rbac';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  userProfile: User | null;
  userId: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  verifyPasscode: (passcode: string) => Promise<boolean>;
  verifyPasscodeByEmail: (email: string, passcode: string) => Promise<boolean>;
  verifyPasscodeDirectly: (passcode: string) => Promise<boolean>;
  isPasscodeAuthenticated: boolean;
  setUserRoleId: (roleId: string) => void;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  userProfile: null,
  userId: null,
  signIn: async () => {},
  signOut: async () => {},
  verifyPasscode: async () => false,
  verifyPasscodeByEmail: async () => false,
  verifyPasscodeDirectly: async () => false,
  isPasscodeAuthenticated: false,
  setUserRoleId: () => {}
});

interface Props {
  children: ReactNode;
}

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPasscodeAuthenticated, setIsPasscodeAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string | null>(getCurrentUserId());

  // Get notification context to reset notifications on login
  const resetNotifications = () => {
    // Access the notification reset function directly from localStorage
    // This avoids circular dependency issues
    const lastLoginKey = 'lastLoginTime';
    localStorage.setItem(lastLoginKey, new Date().toISOString());
    console.log('Notifications reset on login via AuthContext');
  };

  // Load role data for a user
  const loadRoleData = async (user: User): Promise<User> => {
    if (user.roleId) {
      const role = await getRoleById(user.roleId);
      if (role) {
        return { ...user, role };
      }
    }
    return user;
  };

  // Clear any possible invalid auth state on mount
  useEffect(() => {
    // Check for potentially corrupted auth state
    const authSession = localStorage.getItem('authSession');
    const passCodeAuth = localStorage.getItem('isPasscodeAuthenticated');
    const currentUserId = localStorage.getItem('currentUserId');
    
    console.log('Initial auth state check:', {
      hasAuthSession: !!authSession,
      hasPasscodeAuth: passCodeAuth === 'true',
      hasUserId: !!currentUserId,
      path: window.location.pathname
    });
    
    // If we have mismatched auth state, clear it
    if ((passCodeAuth === 'true' && !currentUserId) || 
        (authSession && !currentUserId) ||
        (!authSession && passCodeAuth !== 'true' && currentUserId)) {
      console.log('Detected corrupted auth state - clearing');
      clearAuthSession();
      
      // If we're trying to access a protected page with corrupt state, force auth
      const path = window.location.pathname;
      if (path.startsWith('/staff') || path.startsWith('/admin')) {
        console.log('Redirecting to auth due to corrupted auth state');
        window.location.href = '/auth?forced=true&corrupted=true';
      }
    }
  }, []);

  // Check auth state on mount and periodically
  useEffect(() => {
    const checkAuthState = async () => {
      try {
        console.log('Running periodic auth state check');
        const isValid = await isAuthSessionValid();
        console.log('Auth session valid:', isValid);
        
        if (!isValid) {
          console.log('Auth session invalid - clearing state');
          clearAuthSession();
          setCurrentUser(null);
          setUserProfile(null);
          setIsPasscodeAuthenticated(false);
          setUserId(null);
          
          // Only redirect if we're on a protected page AND the session is explicitly invalid
          const path = window.location.pathname;
          if (path.startsWith('/staff') || path.startsWith('/admin')) {
            // Check if session is explicitly invalid (not just expired)
            const session = getAuthSession();
            if (!session || !session.isValid) {
              console.log('Session explicitly invalid - redirecting');
              window.location.href = '/auth?forced=true&expired=true';
              return;
            }
          }
          
          return;
        }

        const currentUserId = getCurrentUserId();
        if (currentUserId) {
          try {
            const user = await getUserById(currentUserId);
            if (user) {
              const userWithRole = await loadRoleData(user);
              setCurrentUser(userWithRole);
              setUserProfile(userWithRole);
              setUserId(currentUserId);
              
              // Update isPasscodeAuthenticated based on localStorage
              const hasPasscodeAuth = localStorage.getItem('isPasscodeAuthenticated') === 'true';
              setIsPasscodeAuthenticated(hasPasscodeAuth);
            } else {
              console.error('User not found with ID:', currentUserId);
              clearAuthSession();
            }
          } catch (userError) {
            console.error('Error fetching user data:', userError);
            clearAuthSession();
          }
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
        clearAuthSession();
      }
      setLoading(false);
    };

    checkAuthState();

    // Set up periodic check (every 3 minutes)
    const interval = setInterval(checkAuthState, 3 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const result = await userSignIn(email, password);
      
      if (result.success && result.user) {
        // Load role data
        const userWithRole = await loadRoleData(result.user);
        
        // Set current user in state
        setCurrentUser(userWithRole);
        setUserProfile(userWithRole);
        
        // Store user ID and update session with role
        setCurrentUserId(userWithRole.id);
        
        // Set roleId and update auth session for admin and staff
        if (userWithRole.roleId) {
          setUserRoleId(userWithRole.roleId);
          updateAuthSession(true, userWithRole.roleId);
        }
        
        // Reset notifications on successful login
        resetNotifications();
        
        notifyAuthStateChange();
      } else {
        // Authentication failed
        clearAuthSession();
        throw new Error(result.message || 'Authentication failed');
      }
    } catch (error) {
      console.error('Error signing in:', error);
      clearAuthSession();
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      // Clear user state
      setCurrentUser(null);
      setUserProfile(null);
      setIsPasscodeAuthenticated(false);
      // Set logout flag explicitly to ensure session is immediately invalidated
      setLoggedOutState();
      // Clear session storage
      await clearAuthSession();
      notifyAuthStateChange();
      // Force reload to ensure all state is cleared and routes re-evaluate
      window.location.href = '/auth?logout=true';
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const verifyPasscode = async (passcode: string): Promise<boolean> => {
    if (!currentUser) return false;
    return verifyUserPasscode(currentUser.id, passcode);
  };

  const verifyPasscodeByEmailFn = async (email: string, passcode: string): Promise<boolean> => {
    const result = await verifyPasscodeByEmail(email, passcode);
    if (result.isValid && result.userId) {
      const user = await getUserById(result.userId);
      if (user) {
        const userWithRole = await loadRoleData(user);
        setCurrentUser(userWithRole);
        setUserProfile(userWithRole);
        setCurrentUserId(userWithRole.id);
        setIsPasscodeAuthenticated(true);
        return true;
      }
    }
    return false;
  };

  const verifyPasscodeDirectlyFn = async (passcode: string): Promise<boolean> => {
    try {
      setLoading(true);
      // First clear any logout flag since we're actively trying to log in
      localStorage.removeItem('justLoggedOut');
      
      // Empty passcode validation
      if (!passcode || passcode.trim() === '') {
        console.warn('Empty passcode provided');
        return false;
      }
      
      console.log('Verifying passcode...');
      const result = await verifyPasscodeDirectly(passcode);
      console.log('Passcode verification result:', result);

      // Strict validation
      if (!result || typeof result !== 'object') {
        console.warn('Invalid result from passcode verification - not an object');
        return false;
      }
      
      if (result.isValid !== true) {
        console.warn('Passcode verification failed - not valid');
        return false;
      }
      
      if (!result.userId || typeof result.userId !== 'string' || result.userId.trim() === '') {
        console.warn('Passcode verification failed - no valid user ID');
        return false;
      }
      
      console.log('Passcode verified, fetching user with ID:', result.userId);
      
      try {
        const userDocRef = doc(db, 'users', result.userId);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
          console.warn('User document does not exist for userId:', result.userId);
          return false;
        }
        
        const userData = userDoc.data();
        if (!userData) {
          console.warn('User document is empty for userId:', result.userId);
          return false;
        }
        
        // Create user object with required fields
        const user = { ...userData, id: userDoc.id } as User;
        
        // Check if user has required fields
        if (!user.email) {
          console.warn('User is missing required email field');
          return false;
        }
        
        console.log('User data loaded:', user);
        const userWithRole = await loadRoleData(user);
        
        // Verify user has required role
        if (!userWithRole.role || !['staff', 'admin'].includes(userWithRole.role.name.toLowerCase())) {
          console.warn('User does not have required role (staff or admin)');
          return false;
        }
        
        // Set full user state
        setCurrentUser(userWithRole);
        setUserProfile(userWithRole);
        setCurrentUserId(userWithRole.id);
        
        // Set auth session with role ID
        if (userWithRole.roleId) {
          setUserRoleId(userWithRole.roleId);
          updateAuthSession(true, userWithRole.roleId);
          console.log('Updated auth session with roleId from passcode verification:', userWithRole.roleId);
        } else {
          console.warn('User has no role ID, using default');
          updateAuthSession(true);
        }
        
        // Set passcode authenticated flag
        localStorage.setItem('isPasscodeAuthenticated', 'true');
        setIsPasscodeAuthenticated(true);
        
        // Reset notifications on successful passcode login
        resetNotifications();
        
        // Notify other components
        notifyAuthStateChange();
        console.log('Authentication successful via passcode');
        return true;
      } catch (docError) {
        console.error('Error fetching user document:', docError);
        return false;
      }
    } catch (error) {
      console.error('Error in passcode verification:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        loading,
        userProfile,
        userId,
        signIn,
        signOut,
        verifyPasscode,
        verifyPasscodeByEmail: verifyPasscodeByEmailFn,
        verifyPasscodeDirectly: verifyPasscodeDirectlyFn,
        isPasscodeAuthenticated,
        setUserRoleId
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}; 